<template>
  <div class="view" v-if="loaded">
    <div class="col">
      <uiBlockTitle>Страна</uiBlockTitle>
      <uiDropdown
        :options="countriesOptions"
        :value="countriesOptions.find(l => l.value === story.country)"
        @input="setCountry"
      />

      <uiBlockTitle style="margin-top: 10px">Язык</uiBlockTitle>
      <uiDropdown
        :options="langsOptions"
        :value="langsOptions.find(l => l.value === story.lang)"
        @input="setLang"
      />

      <uiBlockTitle style="margin-top: 10px">Название</uiBlockTitle>
      <uiInput v-model="story.title" placeholder="Название" />

      <uiBlockTitle style="margin-top: 10px"> Обложка </uiBlockTitle>
      <div class="cover">
        <div class="tip">180 x 180</div>
        <img class="image" :src="story.cover" v-if="story.cover" />
        <input
          type="file"
          accept="image/*"
          class="drop-area"
          @change="onCover"
        />
      </div>

      <uiDevider />
      <uiBlockTitle>Слайды</uiBlockTitle>
      <draggable
        class="slides"
        :forceFallback="true"
        v-model="story.slides"
        handle=".handle"
        ghost-class="ghost"
      >
        <div
          class="slide"
          :class="{ selected: slide._id === curSlideId }"
          v-for="slide in story.slides"
          :key="slide._id"
        >
          <div class="wrap">
            <img class="image" :src="slide.image" v-if="slide.image" />

            <div class="num handle" @mousedown="curSlideId = slide._id"></div>
            <div
              class="remove"
              v-if="slides.length > 1"
              @click="removeSlide(slide)"
            >
              <i class="lni lni-trash"></i>
            </div>
          </div>
          <div class="dot" v-if="!isSlideValid(slide)" />
        </div>
        <div class="slide add" @click="addSlide">
          <i class="lni lni-plus"></i>
        </div>
      </draggable>

      <uiBlockTitle> Время слайда </uiBlockTitle>
      <div class="slide-time">
        <div
          class="time"
          :class="{ selected: curSlide.time === time * 1000 }"
          @click="setSlideTime(time)"
          v-for="time in slideTimes"
          :key="time"
        >
          {{ time }}
        </div>
      </div>

      <uiDevider />
      <uiButton :disabled="!valid" :loading="loading" @click="saveStory">
        Сохранить
      </uiButton>
      <uiButton
        color="red"
        :disabled="loading"
        @click="removeStory"
        style="margin-top: 10px"
      >
        Удалить
      </uiButton>
    </div>

    <div class="col" style="width: fit-content">
      <div class="story-wrap" :style="{ background: curSlide.bgColor }">
        <div class="story">
          <div class="tip">1080 x 1920</div>
          <img class="image" :src="curSlide.image" v-if="curSlide.image" />
          <video
            class="video"
            :src="curSlide.video"
            v-if="curSlide.video"
            autoplay
          />
          <div class="button" v-if="curSlide.button" :style="buttonStyle">
            {{ curSlide.button.text }}
          </div>

          <input
            type="file"
            accept="image/*"
            class="drop-area"
            @change="onFile"
            ref="input"
          />
        </div>
      </div>
    </div>

    <div class="space-y-3 col">
      <div>
        <uiBlockTitle>Слайд</uiBlockTitle>
        <uiColorPicker label="Цвет фона" v-model="curSlide.bgColor" />
      </div>
      <uiDevider />

      <uiDropdown
        label="Кнопка"
        :options="buttonKinds"
        :value="buttonKinds.find(k => k.value === curSlide?.button?.kind) ?? 'Нет'"
        @input="setSlideButton"
      />

      <template v-if="curSlide.button">
        <uiInput
          v-if="curSlide.button.kind === 'url'"
          v-model="curSlide.button.url"
          label="Ссылка"
        />

        <uiInput
          v-if="curSlide.button.kind === 'webview'"
          v-model="curSlide.button.webview"
          label="Ссылка"
        />

        <uiDropdown
          v-if="curSlide.button.kind === 'articlePage'"
          label="Добавить страницу"
          :options="pages"
          :value="pages.find(k => k.value === curSlide?.button?.article)"
          @input="curSlide.button.article = $event.value"
          clearSearchOnSelect
        />

        <uiDropdown
          v-if="curSlide.button.kind === 'articleLink'"
          label="Добавить статью"
          :options="articles"
          :value="articles.find(k => k.value === curSlide?.button?.article)"
          @input="curSlide.button.article = $event.value"
          clearSearchOnSelect
        />

        <uiInput
          v-model="curSlide.button.text"
          label="Текст на кнопке"
        />
        <div style="display: flex; justify-content: space-between">
          <uiColorPicker label="Цвет фона" v-model="curSlide.button.bgColor" />
          <uiColorPicker
            label="Цвет текста"
            v-model="curSlide.button.textColor"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Draggable from 'vuedraggable'
import StoryView from '@/components/StoryView'
import { useStorage } from '@vueuse/core'
import { API } from '@/config'
import { useCountries } from '@/composables/countries'
import { ref } from 'vue'
import { mapToList } from '@/utils/select'
import { fetchArticles } from '@/api'


const buttonKindMap = {
  '': 'Нет',
  url: 'Сафари',
  webview: 'Вебвью',
  articlePage: 'Страница статей',
  articleLink: 'Ссылка на статью',
  chat: 'Чат с Лапкой',
  dogologyChat: 'Чат с Догологией',
  petScanner: 'Петсканнер',
  petCanEat: 'База продуктов',
  petfriendlyMap: 'Петфрендли места',
  petMedia: 'Медиа',
  docsArchive: 'Архив документов',
  foodDiary: 'Дневник питания',
  petReminders: 'Уведомления',
  userProfile: 'Профиль',
}

const buttonTextMap = {
  url: 'Подробнее',
  webview: 'Подробнее',
  articlePage: 'Почитать',
  articleLink: 'Почитать',
  chat: 'Открыть чат',
  dogologyChat: 'Открыть чат',
  petScanner: 'Открыть сканнер',
  petCanEat: 'Опасные продукты',
  petfriendlyMap: 'Открыть карту',
  petMedia: 'Медиа',
  docsArchive: 'Архив документов',
  foodDiary: 'Дневник питания',
  petReminders: 'Уведомления',
  userProfile: 'Профиль',
}

export default {
  name: 'StoryPage',
  components: {
    Draggable,
    StoryView
  },
  props: {
    id: String
  },
  data () {
    const { countries } = useCountries()
    const activeCountry = useStorage('activeCountry', countries.value[0].code)

    const { langs } = useCountries({ activeCountry })
    const activeLang = useStorage('activeLang', langs.value[0].code)

    return {
      loaded: false,
      loading: false,
      busy: false,
      story: {
        id: 'new',
        title: '',
        cover: null,
        slides: [],
        lang: activeLang.value
      },
      curSlideId: null,
      slideTimes: [5, 10, 15, 20, 25, 30],
      bgColor: '#ffffff',
      fontColor: '#000000',
      uploads: {},
      countries: countries.value,
      langs: langs.value,
      buttonKinds: mapToList(buttonKindMap),
      pages: [],
      articles: [],
    }
  },
  computed: {
    slides () {
      return this.story.slides
    },
    curSlide: {
      get () {
        return this.slides.find(s => s._id === this.curSlideId)
      },
      set (val) {
        let index = this.slides.findIndex(s => s._id === this.curSlideId)
        this.slides.splice(index, 1, val)
      }
    },
    slideButtonArticle () {
      if (!this.curSlide.button) return
      if (this.curSlide.button.kind !== 'article') return
      return this.articles.find(a => a._id === this.curSlide.button.article)
    },
    buttonStyle () {
      return {
        background: this.curSlide.button.bgColor,
        color: this.curSlide.button.textColor
      }
    },
    slidesValid () {
      return !this.slides.map(s => this.isSlideValid(s)).includes(false)
    },
    valid () {
      let s = this.story
      return !!s.title.length && s.cover && this.slidesValid
    },
    countriesOptions () {
      return this.countries.map(country => ({
        value: country.code,
        label: country.name
      }))
    },
    langsOptions () {
      return this.langs.map(lang => ({ value: lang.code, label: lang.name }))
    }
  },
  activated () {
    if (this.id === 'new') {
      const { countries } = useCountries()
      const activeCountry = useStorage('activeCountry', countries.value[0].code)
      const { langs } = useCountries({ activeCountry })
      const activeLang = useStorage('activeLang', langs.value[0].code)

      this.story = {
        id: 'new',
        title: '',
        cover: null,
        slides: [],
        country: activeCountry.value,
        lang: activeLang.value
      }
      this.langs = langs.value
      this.addSlide()
    }
    this.loadStory()
    this.loadPages()
    this.loadArticles()
  },
  methods: {
    async loadStory () {
      if (this.id === 'new') return (this.loaded = true)
      let { data } = await this.$http.get(API.STORIES.get + this.id)
      if (!data || data.err) return this.$notify(data.err)
      this.story = data
      this.curSlideId = this.story.slides[0]._id
      this.loaded = true
    },
    async loadPages () {
      const { data } = await this.$http.get('admin/v2/articles/pages')
      this.pages = (data ?? []).map(page => {
        return {
          value: page.id,
          label: page.title
        }
      })
    },
    async loadArticles () {
      const data = await fetchArticles()
      this.articles = (data ?? []).map(article => {
        return {
          value: article.url,
          label: article.documentTitle
        }
      })
    },
    isSlideValid (s) {
      if (!s.image && !s.video) return false
      if (s.button && s.button.kind === 'url') {
        if (!s.button.url) return false
      }
      if (s.button && s.button.kind === 'webview') {
        if (!s.button.webview) return false
      }
      if (s.button && s.button.kind === 'article') {
        if (!s.button.article) return false
      }
      return true
    },
    addSlide () {
      let id = 'new-' + Math.random().toString().slice(2)
      this.slides.push({
        _id: id,
        time: 5000,
        bgColor: '#000000',
        image: null,
        video: null
      })
      this.curSlideId = id
    },
    removeSlide (slide) {
      if (this.slides.length === 1) return
      if (slide._id !== this.curSlideId) return

      let index = this.slides.findIndex(s => s._id === slide._id)
      if (this.slides.length === 1) {
        this.curSlideId = null
      } else {
        let next = index - 1
        if (next < 0) next = index + 1
        this.curSlideId = this.slides[next]._id
      }
      this.slides.splice(index, 1)
    },
    setSlideTime (time) {
      this.curSlide.time = time * 1000
    },
    setSlideButton ({ value, label }) {
      let fresh = JSON.parse(JSON.stringify(this.curSlide))

      if (!value) {
        fresh.button = null
        this.curSlide = fresh
        return
      }

      let btn = {
        bgColor: '#FFFFFF',
        textColor: '#000000',
        url: null,
        webview: null,
        article: null,
        kind: value,
        text: buttonTextMap[value]
      }

      fresh.button = btn
      this.curSlide = fresh
    },
    setButtonArticle (e) {
      this.curSlide.button.article = e._id
    },
    onFile (e) {
      if (this.busy) return
      this.busy = true

      if (!e.target.files[0]) return (this.busy = false)
      let url = URL.createObjectURL(e.target.files[0])
      let img = new Image()
      img.src = url
      img.onload = () => {
        if (img.width !== 1080 || img.height !== 1920) {
          this.$notify('Размер должен быть 1080x1920')
          URL.revokeObjectURL(url)
        } else {
          this.curSlide.image = url
          this.uploads[this.curSlideId] = e.target.files[0]
          this.$refs.input.value = ''
        }
        this.busy = false
      }
    },
    onCover (e) {
      if (this.busy) return
      this.busy = true

      if (!e.target.files[0]) return (this.busy = false)
      let url = URL.createObjectURL(e.target.files[0])
      let img = new Image()
      img.src = url
      img.onload = () => {
        if (img.width !== 180 || img.height !== 180) {
          this.$notify('Размер должен быть 180x180')
          URL.revokeObjectURL(url)
        } else {
          this.story.cover = url
          this.uploadCover = e.target.files[0]
        }
        this.busy = false
      }
    },
    setCountry (country) {
      const { langs } = useCountries({ activeCountry: ref(country.value) })
      this.langs = langs.value
      this.story.country = country.value
      this.story.lang = langs.value[0].code
    },
    setLang (lang) {
      this.story.lang = lang.value
    },
    async saveStory () {
      if (this.loading) return
      this.loading = true

      let story = JSON.parse(JSON.stringify(this.story))
      let isNew = story.id === 'new'
      if (isNew) delete story.id

      await this.$http.get(API.AUTH.check + '?refresh=true')

      for (let key of Object.keys(this.uploads)) {
        let { url, err } = await this.uploadFile(this.uploads[key])
        if (!url || err) {
          this.loading = false
          return this.$notify(err)
        }
        let index = story.slides.findIndex(s => s._id === key)
        story.slides[index].image = url
      }

      if (this.uploadCover) {
        let { url, err } = await this.uploadFile(this.uploadCover)
        if (!url || err) {
          this.loading = false
          return this.$notify(err)
        }
        story.cover = url
      }

      let { data } = await this.$http.post(API.STORIES.update, story)
      if (!data || data.err) {
        this.loading = false
        return this.$notify(err)
      }

      this.story = data
      this.uploads = {}
      if (isNew) {
        this.$store.commit('addToArray', { path: 'stories', data: data })
        this.curSlideId = this.story.slides[0]._id
        this.$router.replace('/stories/' + data.id)
      }

      this.loading = false
    },
    async uploadFile (file) {
      if (!file) return

      let payload = new FormData()
      payload.append('file', file)
      payload.append('path', 'stories/')

      let { data } = await this.$http.upload(API.SPACES.upload, payload)
      return data
    },
    async removeStory () {
      let confirm = await this.$confirm('Точно удаляем?')
      if (!confirm) return

      if (this.loading) return
      this.loading = true

      let { data } = await this.$http.post(API.STORIES.delete, { id: this.id })
      if (!data || data.err) {
        this.$notify(data.err)
        return (this.loading = false)
      }

      this.$store.commit('removeFromArray', { path: 'stories', data: data.id })
      this.loading = false
      this.$router.push('/stories')
    }
  }
}
</script>

<style lang="less" scoped>
@import '../assets/app.less';

.view {
  display: flex;
  justify-content: center;
}

.col {
  width: 270px;
  margin: 0 15px;
  margin-top: 30px;
}

.cover {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: @grey;
  position: relative;
  overflow: hidden;

  .tip {
    width: 100%;
    text-align: center;
    position: absolute;
    top: 50%;
    font-size: 12px;
    transform: translateY(-50%);
    opacity: 0.5;
  }

  input {
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    position: absolute;
    opacity: 0;
    cursor: pointer;
  }

  .image {
    width: 100%;
    height: 100%;
    position: absolute;
  }
}

.slides {
  width: 100%;
  display: flex;
  flex-wrap: wrap;

  .slide {
    width: 60px;
    height: 107px;
    margin-bottom: 10px;
    margin-right: 10px;
    color: #fff;
    font-size: 20px;
    border-radius: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    box-sizing: border-box;
    cursor: pointer;
    position: relative;
    overflow: hidden;

    .wrap {
      width: 100%;
      height: 100%;
      position: absolute;
      opacity: 0.2;
      background: #000;
    }

    img {
      width: 100%;
      height: 100%;
      position: absolute;
      opacity: 0.5;
    }

    .dot {
      width: 6px;
      height: 6px;
      background: red;
      border-radius: 50%;
      position: absolute;
      left: 6px;
      top: 6px;
    }

    .num {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      text-align: center;
      line-height: 70px;
    }

    .remove {
      width: 100%;
      height: 0%;
      position: absolute;
      bottom: 0;
      text-align: center;
      opacity: 0;
    }

    &.ghost {
      opacity: 0.5;
    }

    &:nth-child(4n) {
      margin-right: 0;
    }

    &.selected .wrap {
      opacity: 1;

      .num {
        height: 65%;
      }

      .remove {
        height: 35%;
        opacity: 0.7;
      }

      .remove:hover {
        opacity: 1;
      }
    }

    &.add {
      background: @grey;
      color: rgba(0, 0, 0, 0.3);
      float: left;
      opacity: 1;

      &:hover {
        color: #000;
      }
    }
  }
}

.slide-time {
  display: flex;
  justify-content: space-between;

  .time {
    flex-grow: 1;
    opacity: 0.3;
    font-size: 20px;
    font-weight: bold;
    cursor: pointer;
    text-align: center;

    &:hover {
      opacity: 0.6;
    }

    &.selected {
      opacity: 1;
    }
  }
}

.story-wrap {
  width: 360px;
  height: 640px;
  background: red;
  box-shadow: 0 5px 40px rgba(0, 0, 0, 0.2);
  background: #000;
  border-radius: 16px;
  padding: 30px 0;
}

.story {
  width: 360px;
  height: 640px;
  position: relative;
  overflow: hidden;

  .image {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
  }

  .tip {
    text-align: center;
    color: #fff;
    position: absolute;
    left: 50%;
    top: 50%;
    font-size: 14px;
    transform: translate(-50%, -50%);
    opacity: 0.5;
  }

  .button {
    padding: 0 20px;
    width: fit-content;
    min-width: 180px;
    max-width: 320px;
    height: 50px;
    line-height: 50px;
    background: #fff;
    position: absolute;
    left: 50%;
    bottom: 8.5%;
    transform: translateX(-50%);
    text-align: center;
    border-radius: 16px;
  }

  .drop-area {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    opacity: 0;
    cursor: pointer;
    outline: none;
  }
}
</style>
